import { useState } from "react";
import { Upload, Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_FILE_MB = 5;

export const Scholarship = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [age, setAge] = useState("");
  const [message, setMessage] = useState("");
  const [cv, setCv] = useState(null);
  const [status, setStatus] = useState({ type: "idle", msg: "" });

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return setCv(null);
    if (f.size > MAX_FILE_MB * 1024 * 1024) {
      setStatus({
        type: "error",
        msg: `File too large. Max ${MAX_FILE_MB}MB.`,
      });
      return;
    }
    setCv(f);
    setStatus({ type: "idle", msg: "" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "loading", msg: "" });
    try {
      const form = new FormData();
      form.append("name", name.trim());
      form.append("email", email.trim());
      form.append("age", age);
      form.append("message", message.trim());
      if (cv) form.append("cv", cv);
      await axios.post(`${API}/scholarship`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStatus({
        type: "success",
        msg: "Application received. Jordan reviews every entry personally.",
      });
      setName("");
      setEmail("");
      setAge("");
      setMessage("");
      setCv(null);
      // reset file input
      const fileInput = document.getElementById("scholarship-cv-input");
      if (fileInput) fileInput.value = "";
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or email Jordan directly.";
      setStatus({ type: "error", msg: detail });
    }
  };

  const isLoading = status.type === "loading";

  return (
    <section
      id="scholarship"
      data-testid="scholarship-section"
      className="relative bg-[#080808] py-24 md:py-32 border-t border-zinc-900"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12 md:mb-14">
          <div className="reveal flex items-center justify-center gap-3 mb-6">
            <span className="h-px w-10 bg-zinc-600" />
            <span className="text-xs tracking-[0.3em] uppercase text-[#EDE6D5] font-bold">
              Free Scholarship · Under 25
            </span>
            <span className="h-px w-10 bg-zinc-600" />
          </div>
          <h2
            data-testid="scholarship-headline"
            className="reveal text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-extrabold text-white leading-[1.05]"
          >
            One free spot, every cohort.{" "}
            <span className="italic font-light text-white">Make it yours.</span>
          </h2>
          <p
            data-testid="scholarship-subheadline"
            className="reveal mt-6 max-w-2xl mx-auto text-base md:text-lg leading-relaxed text-zinc-400 font-medium"
          >
            Every cohort I give away one free space in The Builder Programme to
            someone under 25. Apply here and tell me why it should be you. The
            floor is yours — be as comprehensive as possible.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="scholarship-form"
          className="reveal bg-[#0A0A0A] border border-zinc-900 p-6 md:p-10 space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label
                htmlFor="s-name"
                className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2"
              >
                Full Name
              </label>
              <input
                id="s-name"
                data-testid="scholarship-name-input"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isLoading}
                className="w-full bg-transparent border-b border-zinc-800 focus:border-white text-white py-3 outline-none transition-colors disabled:opacity-50"
              />
            </div>
            <div>
              <label
                htmlFor="s-email"
                className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2"
              >
                Email
              </label>
              <input
                id="s-email"
                data-testid="scholarship-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                className="w-full bg-transparent border-b border-zinc-800 focus:border-white text-white py-3 outline-none transition-colors disabled:opacity-50"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="s-age"
              className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2"
            >
              Age (must be under 25)
            </label>
            <input
              id="s-age"
              data-testid="scholarship-age-input"
              type="number"
              min="16"
              max="24"
              required
              value={age}
              onChange={(e) => setAge(e.target.value)}
              disabled={isLoading}
              className="w-full md:w-40 bg-transparent border-b border-zinc-800 focus:border-white text-white py-3 outline-none transition-colors disabled:opacity-50"
            />
          </div>

          <div>
            <label
              htmlFor="s-message"
              className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2"
            >
              Why should it be you?
            </label>
            <textarea
              id="s-message"
              data-testid="scholarship-message-input"
              required
              rows={7}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isLoading}
              placeholder="The floor is yours — what you're building, what you're stuck on, what this spot would unlock."
              className="w-full bg-transparent border border-zinc-800 focus:border-white text-white p-4 outline-none transition-colors resize-none placeholder:text-zinc-700 disabled:opacity-50"
            />
          </div>

          <div>
            <label
              htmlFor="scholarship-cv-input"
              data-testid="scholarship-cv-label"
              className="group flex items-center gap-3 cursor-pointer border border-dashed border-zinc-800 hover:border-zinc-600 transition-colors p-4 text-zinc-400 hover:text-white"
            >
              <Upload className="w-4 h-4" strokeWidth={1.5} />
              <span className="text-xs tracking-[0.2em] uppercase font-bold flex-1">
                {cv ? cv.name : `Attach CV / portfolio (optional, max ${MAX_FILE_MB}MB)`}
              </span>
              <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-600 font-bold">
                PDF · DOC · DOCX
              </span>
            </label>
            <input
              id="scholarship-cv-input"
              data-testid="scholarship-cv-input"
              type="file"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={handleFile}
              disabled={isLoading}
              className="hidden"
            />
          </div>

          {status.type === "error" && (
            <div
              data-testid="scholarship-error"
              className="flex items-start gap-3 text-red-300 text-sm border border-red-900/50 bg-red-950/30 p-4"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{status.msg}</span>
            </div>
          )}

          {status.type === "success" && (
            <div
              data-testid="scholarship-success"
              className="flex items-start gap-3 text-[#EDE6D5] text-sm border border-[#EDE6D5]/30 bg-[#EDE6D5]/5 p-4"
            >
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{status.msg}</span>
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
            <p className="text-xs tracking-[0.2em] uppercase text-zinc-600 font-bold">
              Application Only · One Spot Per Cohort
            </p>
            <button
              type="submit"
              data-testid="scholarship-submit-btn"
              disabled={isLoading}
              className="group inline-flex items-center justify-center gap-3 bg-white text-black font-bold text-sm tracking-[0.15em] uppercase px-7 py-4 hover:bg-zinc-200 transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting…
                </>
              ) : (
                <>
                  Submit Application
                  <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};
